/**
 * Circularity Calculator Service
 *
 * Computes a simplified per-material Circularity Indicator on a 0–1 scale.
 * This is a demo proxy, NOT a standards-compliant Ellen MacArthur MCI —
 * end-of-life fate, recycling efficiency, and product lifetime are not
 * modeled.
 *
 *   index = 0.5 × recycled_input_fraction + 0.5 × recovery_output_fraction
 *
 *   - recycled_input_fraction: share of input mass (per material) from
 *     recycled / secondary sources, detected via input-name keywords
 *     (recycl/secondary/scrap/recover/reuse/reclaim).
 *   - recovery_output_fraction: share of material recovered as product
 *     output vs feedstock (for recycling projects) or vs total input
 *     (for other projects). Only counted when the project category is
 *     "recycling" OR the product name itself indicates recovered /
 *     secondary material — designed manufacturing outputs are excluded.
 *
 * Elemental decomposition:
 *   NMC cathode inputs and recovered NMC outputs (NMC111 / NMC532 /
 *   NMC622 / NMC811 and variants) are split into Li / Ni / Mn / Co by
 *   molecular composition. Earlier versions matched "nmc" as a keyword
 *   for all three of Ni/Mn/Co, which triple-counted the mass.
 */

/**
 * Tracked battery materials and keywords to match them in LCI input/product names.
 * Each entry: { label, keywords (lowercase), element for display }
 *
 * IMPORTANT: 'nmc' is NOT a direct keyword here. NMC cathodes are detected
 * separately by parseNMCRatio() and decomposed into Li/Ni/Mn/Co by molecular
 * composition — otherwise 1 kg of "NMC811" would be counted as 1 kg of Co,
 * 1 kg of Ni, and 1 kg of Mn (triple-count bug).
 */
const TRACKED_MATERIALS = {
  lithium:   { label: 'Lithium',   keywords: ['lithium', 'li2co3', 'licoo2', 'lipf6', 'li_solution', 'li-ion', 'relithiated'], element: 'Li' },
  cobalt:    { label: 'Cobalt',    keywords: ['cobalt'], element: 'Co' },
  nickel:    { label: 'Nickel',    keywords: ['nickel'], element: 'Ni' },
  manganese: { label: 'Manganese', keywords: ['manganese'], element: 'Mn' },
  graphite:  { label: 'Graphite',  keywords: ['graphite'], element: 'C' },
  copper:    { label: 'Copper',    keywords: ['copper', 'cu foil'], element: 'Cu' },
  aluminum:  { label: 'Aluminum',  keywords: ['aluminum', 'aluminium', 'al foil'], element: 'Al' },
  steel:     { label: 'Steel',     keywords: ['steel', 'iron'], element: 'Fe' },
};

// Atomic weights (g/mol) for NMC decomposition
const AW = { Li: 6.94, Ni: 58.69, Mn: 54.94, Co: 58.93, O: 16.00 };

/**
 * Detect an NMC chemistry in a name and return normalized molar ratios.
 * Matches "NMC111", "NMC532", "NMC622", "NMC811", and variants like
 * "nmc_811" / "nmc 811". Returns null if not an NMC input.
 */
function parseNMCRatio(text) {
  const m = /nmc[_\s-]*(\d)(\d)(\d)/i.exec(text || '');
  if (!m) return null;
  const ni = parseInt(m[1], 10);
  const mn = parseInt(m[2], 10);
  const co = parseInt(m[3], 10);
  const sum = ni + mn + co;
  if (!sum) return null;
  return { ni: ni / sum, mn: mn / sum, co: co / sum };
}

/**
 * Given 1 kg of NMC active material with molar ratios (ni:mn:co), return
 * mass fractions for each element. Formula assumed: Li Ni_x Mn_y Co_z O_2
 * with x+y+z=1.
 */
function nmcMassFractions(ratio) {
  if (!ratio) return null;
  const { ni, mn, co } = ratio;
  const mw = AW.Li + ni * AW.Ni + mn * AW.Mn + co * AW.Co + 2 * AW.O;
  return {
    Li: AW.Li / mw,
    Ni: (ni * AW.Ni) / mw,
    Mn: (mn * AW.Mn) / mw,
    Co: (co * AW.Co) / mw,
  };
}

/**
 * Check if a name matches a tracked material (case-insensitive keyword match).
 */
function matchesMaterial(name, keywords) {
  const lower = (name || '').toLowerCase();
  return keywords.some(kw => lower.includes(kw));
}

/**
 * Check whether a product (designed output) represents recovered / secondary
 * material rather than freshly manufactured material. True if the project is
 * a recycling project OR the product name itself says so.
 */
function isRecoveredOutput(productName, isRecyclingProject) {
  if (isRecyclingProject) return true;
  return /recycl|recover|reclaim|secondary|scrap|reuse/i.test(productName || '');
}

/**
 * Determine if an LCI input is from a recycled/secondary source.
 * Heuristic: check for keywords like "recycled", "secondary", "scrap", "recovered".
 */
function isRecycledSource(input) {
  const text = `${input.name || ''} ${input.activity_name || ''} ${input.reference_product || ''}`.toLowerCase();
  return /recycl|secondary|scrap|recover|reuse|reclaim/.test(text);
}

/**
 * Run Circularity analysis.
 *
 * Computes a Material Circularity Index (MCI) per tracked material on a 0–1 scale:
 *   MCI = (recycled_input_fraction × 0.5) + (recovery_output_fraction × 0.5)
 *
 * - recycled_input_fraction: share of input mass from recycled/secondary sources
 * - recovery_output_fraction: share of material recovered as products vs feedstock mass
 *
 * For manufacturing (linear): mostly 0 unless recycled inputs are used.
 * For recycling (BRAWS): recovery rates push MCI toward 1.
 */
export function runCircularity(project) {
  if (!project || !project.processes) {
    return { success: false, error: 'No project data' };
  }

  const processes = project.processes;
  const category = (project.project_info?.category || '').toLowerCase();
  const isRecyclingProject = category.includes('recycl');
  const feedstockWeight = project.functional_unit?.weight_kg || 0;

  // Accumulate per-material data
  const materialData = {};
  for (const [matKey, matInfo] of Object.entries(TRACKED_MATERIALS)) {
    materialData[matKey] = {
      label: matInfo.label,
      element: matInfo.element,
      virgin_input_kg: 0,
      recycled_input_kg: 0,
      recovered_output_kg: 0,
      total_input_kg: 0,
      processes_involved: new Set(),
    };
  }

  // Helper: add mass to a material's bucket (virgin vs recycled) and tracking set
  const addInputMass = (matKey, massKg, recycled, procLabel) => {
    if (massKg <= 0) return;
    if (recycled) {
      materialData[matKey].recycled_input_kg += massKg;
    } else {
      materialData[matKey].virgin_input_kg += massKg;
    }
    materialData[matKey].total_input_kg += massKg;
    materialData[matKey].processes_involved.add(procLabel);
  };

  // Scan all processes
  for (const [procId, proc] of Object.entries(processes)) {
    const procLabel = proc.name || procId;

    // -- LCI inputs: material consumption --
    for (const input of (proc.lci_inputs || [])) {
      const unit = (input.unit || '').toLowerCase();
      const isMassUnit = unit.includes('kg') || unit.includes('kilogram') || unit.includes('gram');
      if (!isMassUnit) continue; // skip energy (kWh), volume (L), etc.

      const qty = input.quantity || 0;
      const massKg = unit.includes('gram') && !unit.includes('kilogram') ? qty / 1000 : qty;
      const recycled = isRecycledSource(input);

      // NMC cathode? Decompose into Li/Ni/Mn/Co by molecular composition instead
      // of triple-counting the mass against each metal keyword.
      const nmcText = `${input.name || ''} ${input.activity_name || ''} ${input.reference_product || ''}`;
      const nmcRatio = parseNMCRatio(nmcText);
      if (nmcRatio) {
        const frac = nmcMassFractions(nmcRatio);
        addInputMass('lithium',   massKg * frac.Li, recycled, procLabel);
        addInputMass('nickel',    massKg * frac.Ni, recycled, procLabel);
        addInputMass('manganese', massKg * frac.Mn, recycled, procLabel);
        addInputMass('cobalt',    massKg * frac.Co, recycled, procLabel);
        continue; // don't also match generic keywords for NMC inputs
      }

      // Non-NMC input: fall back to single-keyword match per material
      for (const [matKey, matInfo] of Object.entries(TRACKED_MATERIALS)) {
        if (matchesMaterial(input.name, matInfo.keywords) ||
            matchesMaterial(input.activity_name || '', matInfo.keywords) ||
            matchesMaterial(input.reference_product || '', matInfo.keywords)) {
          addInputMass(matKey, massKg, recycled, procLabel);
        }
      }
    }

    // -- Products: only count as "recovered" if the project is a recycling
    //    project OR the product name itself indicates recovered/secondary material.
    //    For pure manufacturing, designed products are NOT recovered material and
    //    must not be credited as such. --
    for (const product of (proc.products || [])) {
      const productName = product.material || product.name || '';
      if (!isRecoveredOutput(productName, isRecyclingProject)) continue;

      // Recovered NMC? Decompose into elements too.
      const nmcRatio = parseNMCRatio(productName);
      if (nmcRatio) {
        const frac = nmcMassFractions(nmcRatio);
        const qty = product.quantity || 0;
        materialData.lithium.recovered_output_kg   += qty * frac.Li;
        materialData.nickel.recovered_output_kg    += qty * frac.Ni;
        materialData.manganese.recovered_output_kg += qty * frac.Mn;
        materialData.cobalt.recovered_output_kg    += qty * frac.Co;
        ['lithium','nickel','manganese','cobalt'].forEach(k =>
          materialData[k].processes_involved.add(procLabel));
        continue;
      }

      for (const [matKey, matInfo] of Object.entries(TRACKED_MATERIALS)) {
        if (matchesMaterial(productName, matInfo.keywords)) {
          const qty = product.quantity || 0;
          materialData[matKey].recovered_output_kg += qty;
          materialData[matKey].processes_involved.add(procLabel);
        }
      }
    }
  }

  // Compute MCI per material
  const materials = [];
  let totalWeightedMCI = 0;
  let totalMass = 0;

  for (const [matKey, data] of Object.entries(materialData)) {
    // Skip materials not present in this project
    if (data.total_input_kg === 0 && data.recovered_output_kg === 0) continue;

    // Recycled input fraction (0–1)
    const recycledFraction = data.total_input_kg > 0
      ? data.recycled_input_kg / data.total_input_kg
      : 0;

    // Recovery fraction (0–1)
    // For recycling projects: recovered / feedstock weight (capped at 1)
    // For manufacturing: recovered / total input (usually 0)
    let recoveryFraction = 0;
    if (data.recovered_output_kg > 0) {
      const denominator = isRecyclingProject
        ? (feedstockWeight > 0 ? feedstockWeight : data.total_input_kg || 1)
        : (data.total_input_kg || 1);
      recoveryFraction = Math.min(data.recovered_output_kg / denominator, 1.0);
    }

    // MCI = weighted combination
    const mci = (recycledFraction * 0.5) + (recoveryFraction * 0.5);

    const materialMass = data.total_input_kg + data.recovered_output_kg;
    totalWeightedMCI += mci * materialMass;
    totalMass += materialMass;

    materials.push({
      key: matKey,
      label: data.label,
      element: data.element,
      mci: Math.round(mci * 1000) / 1000,
      recycled_input_fraction: Math.round(recycledFraction * 1000) / 1000,
      recovery_fraction: Math.round(recoveryFraction * 1000) / 1000,
      virgin_input_kg: Math.round(data.virgin_input_kg * 100) / 100,
      recycled_input_kg: Math.round(data.recycled_input_kg * 100) / 100,
      recovered_output_kg: Math.round(data.recovered_output_kg * 100) / 100,
      total_input_kg: Math.round(data.total_input_kg * 100) / 100,
      processes: Array.from(data.processes_involved),
    });
  }

  // Sort by MCI descending, then by label
  materials.sort((a, b) => b.mci - a.mci || a.label.localeCompare(b.label));

  const overallMCI = totalMass > 0
    ? Math.round((totalWeightedMCI / totalMass) * 1000) / 1000
    : 0;

  return {
    success: true,
    overall_mci: overallMCI,
    is_recycling: isRecyclingProject,
    feedstock_weight_kg: feedstockWeight,
    materials,
    note: isRecyclingProject
      ? 'Recovery fractions use recovered-product mass vs feedstock weight. Simplified indicator — not EMF-compliant MCI.'
      : 'Manufacturing project — designed products are not counted as recovered material. Circularity rises only when recycled feedstock is used or outputs are labeled recovered/secondary/reclaimed. Simplified indicator.',
  };
}

export { TRACKED_MATERIALS };
export default { runCircularity, TRACKED_MATERIALS };
